import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Form, Input, Button, message, Card, Typography, Alert } from 'antd';
import { Link } from 'react-router-dom';
import { RESEND_SIGN_UP_TOKEN } from '../../graphql/mutations/mutations';
import { AuthLayout } from '../../layouts/AuthLayout';

const { Title } = Typography;

interface ResendSignUpTokenFormValues {
  email: string;
}

const EmailSent: React.FC<{ email: string }> = ({ email }) => {
  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Check your email</Title>
      <p>We&apos;ve sent a new confirmation email to {email}</p>
      <Link to="/login">
        <Button type="primary" style={{ width: '100%' }}>
          Go to login
        </Button>
      </Link>
    </Card>
  );
};

const ResendSignUpTokenForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [sentEmail, setSentEmail] = useState<string>();

  const [resendSignUpToken] = useMutation(RESEND_SIGN_UP_TOKEN, {
    onCompleted: data => {
      if (data.resendSignUpToken) {
        message.success('Confirmation email sent! Please check your email.');
        setSentEmail(form.getFieldValue('email'));
      } else {
        message.error('Failed to send confirmation email');
      }
      setLoading(false);
    },
    onError: error => {
      const errorMessage = error.graphQLErrors[0]?.message || 'Failed to send confirmation email';
      setError(errorMessage);
      setLoading(false);
    },
  });

  const handleSubmit = (values: ResendSignUpTokenFormValues) => {
    setLoading(true);
    setError(undefined);

    resendSignUpToken({
      variables: {
        email: values.email,
      },
    });
  };

  if (sentEmail) {
    return <EmailSent email={sentEmail} />;
  }

  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Resend confirmation</Title>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: '1.5rem' }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          email: '',
        }}>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}>
          <Input placeholder="your@email.com" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 'unset' }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: '100%', marginBottom: '10px' }}>
            Send confirmation email
          </Button>
          <Link to="/login">Log in</Link>
        </Form.Item>
      </Form>
    </Card>
  );
};

export const ResendSignUpToken: React.FC = () => {
  return (
    <AuthLayout>
      <ResendSignUpTokenForm />
    </AuthLayout>
  );
};
